import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SuperAdmin } from '@/api/superadmins';

const defaultValues = {
  name: '',
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be at most 100 characters'),
});

type FormData = typeof defaultValues;

interface EditSuperAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string) => void;
  admin: SuperAdmin | null;
  loading?: boolean;
}

export function EditSuperAdminDialog({
  open,
  onOpenChange,
  onSave,
  admin,
  loading = false,
}: EditSuperAdminDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  // Reset form when dialog opens or admin changes
  useEffect(() => {
    if (open && admin) {
      reset({ name: admin.name || '' });
    }
  }, [open, admin, reset]);

  const onSubmit = (data: FormData) => {
    onSave(data.name.trim());
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset(defaultValues);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Edit Super Admin</DialogTitle>
            <DialogDescription>
              Update the profile for {admin?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="name" className="text-sm font-medium">
                Name
              </label>
              <Input
                id="name"
                type="text"
                placeholder="Enter name"
                {...register('name')}
                hasError={!!errors.name}
                disabled={loading}
              />
              {errors.name?.message && (
                <p className="text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
